import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { toast } from '@/shared/hooks/use-toast';
import { type Banner, type UpdateBannerPayload } from '../types';
import { bannerApi } from '../api';
import { bannerKeys } from './banner.keys';

interface UpdateBannerVariables {
  id: string;
  payload: UpdateBannerPayload;
}

export function useUpdateBanner(
  options?: UseMutationOptions<Banner, Error, UpdateBannerVariables>
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ['banners', 'update'],
    mutationFn: ({ id, payload }: UpdateBannerVariables) => bannerApi.update(id, payload),
    ...mutationOptions,
    onSuccess: (banner, variables, onMutateResult, context) => {
      toast({ title: 'Banner atualizado com sucesso', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: bannerKeys.all });
      providedOnSuccess?.(banner, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao atualizar banner',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });
  const updateBanner = (id: string, payload: UpdateBannerPayload) =>
    mutation.mutateAsync({ id, payload });
  return {
    updateBanner,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}
